import React from 'react';
import {
  HiPhone,
  HiCheckCircle,
  HiClock,
  HiChartBar,
  HiLightningBolt,
  HiExclamation,
} from 'react-icons/hi';
import { CallSummary, Insights } from '../../services/api';

interface AnalyticsMetricCardsProps {
  callSummary: CallSummary;
  insights: Insights;
}

interface MetricCard {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

const AnalyticsMetricCards: React.FC<AnalyticsMetricCardsProps> = ({
  callSummary,
  insights,
}) => {
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value: number): string => {
    return `${Math.round(value)}%`;
  };

  const getQualityColor = (quality: string): { bg: string; icon: string } => {
    switch (quality) {
      case 'high':
        return { bg: 'bg-green-100', icon: 'text-green-600' };
      case 'medium':
        return { bg: 'bg-yellow-100', icon: 'text-yellow-600' };
      case 'low':
        return { bg: 'bg-red-100', icon: 'text-red-600' };
      default:
        return { bg: 'bg-gray-100', icon: 'text-gray-600' };
    }
  };

  const qualityColors = getQualityColor(insights.data_quality_summary.overall_quality);

  const metricCards: MetricCard[] = [
    {
      title: 'Total Calls Analyzed',
      value: callSummary.analyzed_calls,
      subtitle: `of ${callSummary.total_calls} total`,
      icon: <HiPhone className="h-6 w-6" />,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Analysis Success Rate',
      value: formatPercentage(callSummary.analysis_success_rate * 100),
      subtitle: callSummary.failed_analyses > 0 
        ? `${callSummary.failed_analyses} failed` 
        : 'All successful',
      icon: <HiCheckCircle className="h-6 w-6" />,
      bgColor: callSummary.analysis_success_rate >= 0.9 
        ? 'bg-green-100' 
        : callSummary.analysis_success_rate >= 0.7 
        ? 'bg-yellow-100' 
        : 'bg-red-100',
      iconColor: callSummary.analysis_success_rate >= 0.9 
        ? 'text-green-600' 
        : callSummary.analysis_success_rate >= 0.7 
        ? 'text-yellow-600' 
        : 'text-red-600',
    },
    {
      title: 'Avg Call Duration',
      value: formatDuration(callSummary.avg_call_duration),
      subtitle: 'per conversation',
      icon: <HiClock className="h-6 w-6" />,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      title: 'Data Quality',
      value: insights.data_quality_summary.overall_quality.toUpperCase(),
      subtitle: `${formatPercentage(insights.data_quality_summary.completeness_rate)} complete`,
      icon: <HiChartBar className="h-6 w-6" />,
      bgColor: qualityColors.bg,
      iconColor: qualityColors.icon,
    },
  ];

  // Add high priority completion if available
  if (insights.completion_rate_by_priority?.high !== undefined) {
    metricCards.push({
      title: 'High Priority Metrics',
      value: formatPercentage(insights.completion_rate_by_priority.high),
      subtitle: 'completion rate',
      icon: <HiLightningBolt className="h-6 w-6" />,
      bgColor: insights.completion_rate_by_priority.high >= 80 
        ? 'bg-green-100' 
        : insights.completion_rate_by_priority.high >= 60 
        ? 'bg-yellow-100' 
        : 'bg-red-100',
      iconColor: insights.completion_rate_by_priority.high >= 80 
        ? 'text-green-600' 
        : insights.completion_rate_by_priority.high >= 60 
        ? 'text-yellow-600' 
        : 'text-red-600',
    });
  }

  // Add confidence score if available
  if (insights.data_quality_summary?.avg_confidence !== undefined) {
    metricCards.push({
      title: 'Avg Confidence',
      value: formatPercentage(insights.data_quality_summary.avg_confidence * 100),
      subtitle: 'extraction confidence',
      icon: <HiExclamation className="h-6 w-6" />,
      bgColor: insights.data_quality_summary.avg_confidence >= 0.8 
        ? 'bg-green-100' 
        : insights.data_quality_summary.avg_confidence >= 0.6 
        ? 'bg-yellow-100' 
        : 'bg-orange-100',
      iconColor: insights.data_quality_summary.avg_confidence >= 0.8 
        ? 'text-green-600' 
        : insights.data_quality_summary.avg_confidence >= 0.6 
        ? 'text-yellow-600' 
        : 'text-orange-600',
    });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
      {metricCards.slice(0, 4).map((card, index) => (
        <div
          key={index}
          className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)] hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                {card.title}
              </p>
              <div className="mt-2 flex items-baseline">
                <p className="text-2xl font-bold text-[var(--foreground)]">
                  {card.value}
                </p>
              </div>
              {card.subtitle && (
                <p className="mt-1 text-sm text-[var(--foreground-muted)]">
                  {card.subtitle}
                </p>
              )}
            </div>
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${card.bgColor}`}>
              <div className={card.iconColor}>
                {card.icon}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnalyticsMetricCards;
